import { CategoryBadge } from '@/components/ui/category-badge';
import { ConfidenceBadge } from '@/components/ui/confidence-badge';
import { ImportanceBadge } from '@/components/ui/importance-badge';
import { SourceLinks } from '@/components/ui/source-links';
import { Sparkles } from 'lucide-react';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { DiscoveryCard } from './discovery-card';
import culturalThemeThumbnail from '@/assets/cultural-theme-thumbnail.png';
import { cn } from '@/lib/utils';

interface CulturalPointCardProps {
    point: CultureByPassage['keyPoints'][0];
    bookName?: string;
    isExpanded: boolean;
    onToggleExpansion: () => void;
}

export function CulturalPointCard({
    point,
    bookName,
    isExpanded,
    onToggleExpansion,
}: CulturalPointCardProps) {
    const isDarkMode = useDarkMode();

    return (
        <DiscoveryCard
            isExpanded={isExpanded}
            onToggle={onToggleExpansion}
            thumbnailUrl={point.image?.imageUrl || undefined}
            header={
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 flex-1 relative z-10">
                    <div className="flex flex-col gap-3 flex-1 min-w-0">
                        <div className="flex flex-col gap-1.5">
                            {/* Metadata Row */}
                            {point.verseRef && (
                                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/40">
                                    {bookName} {point.verseRef}
                                </span>
                            )}

                            <h4 className="text-xl font-serif font-medium tracking-tight text-foreground/90 leading-tight">
                                {point.title}
                            </h4>
                        </div>

                        {point.summary && (
                            <div className="space-y-3">
                                <p className={`text-sm text-muted-foreground leading-relaxed italic font-light ${!isExpanded ? 'line-clamp-4' : ''}`}>
                                    {point.summary}
                                </p>
                                {!isExpanded && (
                                    <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.15em] text-primary/40 group-hover/card:text-primary/70 transition-colors">
                                        <Sparkles className="h-2.5 w-2.5" />
                                        <span>Discover Significance</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Image Preview (Sidecar) */}
                    {point.image?.imageUrl && <div className={cn(
                        "flex-shrink-0 w-full sm:w-32 md:w-40 transition-opacity duration-500",
                        isExpanded ? "opacity-40" : "opacity-100"
                    )}>
                        <div className="relative group/image overflow-hidden rounded-lg border border-border/40 shadow-sm bg-muted/20 aspect-video flex items-center justify-center p-1">
                            <img
                                src={point.image?.imageUrl}
                                alt={point.title}
                                className={cn(
                                    "w-full h-full transition-transform duration-700 group-hover/image:scale-110",
                                    point.image?.imageUrl ? "object-cover" : "object-contain opacity-90"
                                )}
                                onClick={point.image?.imageUrl ? (e) => {
                                    e.stopPropagation();
                                    window.open(point.image!.imageUrl, '_blank');
                                } : undefined}
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover/image:bg-black/10 transition-colors pointer-events-none" />
                        </div>
                    </div>}
                </div>
            }
        >
            <div className="space-y-6 mt-6 relative z-10">


                {point.description && (
                    <div className="text-sm leading-relaxed text-foreground/80 font-light">
                        {point.description}
                    </div>
                )}

                {/* Relevance Section - Branded Highlight */}
                {point.relevance && (
                    <div className="p-4 rounded-lg bg-primary/[0.03] border border-primary/10 space-y-2 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60">
                            {point.verseRef ? 'Passage Relevance' : 'Biblical Relevance'}
                        </div>
                        <p className="text-sm leading-relaxed text-foreground/80 italic font-serif">
                            {point.relevance}
                        </p>
                    </div>
                )}
            </div>

            {/* Footer Metadata */}
            <div className="flex flex-wrap items-center justify-between gap-4 pt-5 border-t border-border/10">
                <div className="flex items-center gap-3 flex-wrap">
                    {point.importance && (
                        <ImportanceBadge
                            importance={point.importance}
                            context="cultural"
                            className="h-4 text-[9px] px-1.5"
                        />
                    )}
                    {point.category && (
                        <CategoryBadge category={point.category} variant="outline" className="text-[9px] uppercase tracking-widest bg-transparent border-primary/20 text-primary/70" />
                    )}
                    {point.confidence && (
                        <ConfidenceBadge confidence={point.confidence} context="general" className="h-4 text-[9px]" />
                    )}
                </div>

                {point.sources?.length > 0 && (
                    <SourceLinks sources={point.sources} />
                )}
            </div>
        </DiscoveryCard>
    );
}
